import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { Settings, RotateCcw, AlertTriangle, ChevronDown, type LucideIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { EASE_EXPO_OUT, DURATION_FAST, DURATION_MEDIUM } from '@/lib/motion';

export interface SurchargeSummaryToken {
  label: string;
  value: string;
}

export interface SurchargeSectionConfig {
  key: string;
  title: string;
  icon: LucideIcon;
  component: React.ComponentType<Record<string, unknown>>;
  isCustomized?: boolean;
  description: string;
  isChecked: boolean;
  onCheckedChange: (checked: boolean) => void;
  summaryTokens?: SurchargeSummaryToken[];
}

interface SurchargeConfigFormProps {
  title: string;
  description: string;
  sections: SurchargeSectionConfig[];
  hasAnyCustomization?: boolean;
  customizationCount?: number;
  onRevertSection?: (sectionKey: string) => void;
  onRevertAll?: () => void;
  commonProps: Record<string, unknown>;
  className?: string;
  variant?: 'user' | 'client';
  noCard?: boolean;
}

export const SurchargeConfigForm: React.FC<SurchargeConfigFormProps> = ({
  title,
  description,
  sections,
  hasAnyCustomization = false,
  customizationCount = 0,
  onRevertAll,
  commonProps,
  className,
  variant = 'user',
  noCard = false,
}) => {
  const isClientVariant = variant === 'client';

  // Track which sections are expanded (only relevant when isChecked)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    sections.forEach(s => { if (s.isChecked) initial[s.key] = true; });
    return initial;
  });

  // Auto-expand when a section is newly enabled, collapse when disabled
  const prevCheckedMap = useRef(new Map<string, boolean>(
    sections.map(s => [s.key, s.isChecked])
  ));

  const checkedKey = sections.map(s => s.isChecked ? '1' : '0').join('');
  useEffect(() => {
    const updates: Record<string, boolean> = {};
    let hasUpdates = false;
    sections.forEach(s => {
      const wasChecked = prevCheckedMap.current.get(s.key) ?? false;
      if (s.isChecked && !wasChecked) {
        updates[s.key] = true;
        hasUpdates = true;
      } else if (!s.isChecked && wasChecked) {
        updates[s.key] = false;
        hasUpdates = true;
      }
      prevCheckedMap.current.set(s.key, s.isChecked);
    });
    if (hasUpdates) {
      setExpandedSections(prev => ({ ...prev, ...updates }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- checkedKey is a stable string derived from sections' isChecked values
  }, [checkedKey]);

  const toggleExpand = (key: string) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const headerBars = (
    <div className={cn(
      "border border-border rounded bg-card dark:bg-transparent shadow-sm overflow-hidden",
      noCard && "w-full"
    )}>
      {sections.map((section, index) => {
        const IconComponent = section.icon;
        const Component = section.component;
        const isExpanded = section.isChecked && (expandedSections[section.key] ?? false);
        const hasSummary = section.isChecked && section.summaryTokens && section.summaryTokens.length > 0;

        return (
          <div key={section.key}>
            {/* Border separator between sections */}
            {index > 0 && <div className="border-t border-border/40" />}

            {/* Header bar */}
            <div
              className={cn(
                'flex items-center px-3 py-2.5 min-h-[44px] bg-muted transition-colors',
                isExpanded && 'border-b-2 border-b-border',
              )}
            >
              {/* Left: clickable expand/collapse area */}
              <button
                type="button"
                onClick={() => section.isChecked && toggleExpand(section.key)}
                disabled={!section.isChecked}
                aria-expanded={section.isChecked ? isExpanded : undefined}
                className={cn(
                  'flex-1 flex items-center gap-2 min-w-0 text-left',
                  section.isChecked && 'cursor-pointer',
                  !section.isChecked && 'cursor-default',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset rounded-sm'
                )}
              >
                <IconComponent className={cn(
                  "w-3.5 h-3.5 flex-shrink-0 transition-colors duration-200",
                  section.isChecked ? "text-muted-foreground" : "text-muted-foreground/40"
                )} />
                <span className={cn(
                  "font-heading text-sm font-semibold uppercase tracking-wide transition-colors duration-200 flex-shrink-0",
                  section.isChecked ? "text-foreground" : "text-muted-foreground/50"
                )}>
                  {section.title}
                </span>
                {isClientVariant && section.isCustomized && (
                  <Badge variant="outline" className="text-[11px] sm:text-[12px] flex-shrink-0">Custom</Badge>
                )}
                {/* Summary tokens (visible when checked, hidden on mobile) */}
                {hasSummary && (
                  <span className={cn(
                    "hidden sm:inline text-[11px] sm:text-[12px] font-mono truncate text-center flex-1 px-1",
                    isExpanded ? "text-muted-foreground/40" : "text-muted-foreground"
                  )}>
                    <AnimatePresence mode="popLayout">
                      {section.summaryTokens!.map((t) => (
                        <motion.span
                          key={t.label}
                          initial={{ opacity: 0, scale: 0.85 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.85 }}
                          transition={{ duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT }}
                          className="inline-flex whitespace-nowrap origin-left"
                        >
                          <span className="text-muted-foreground/30 mx-1">·</span>
                          <span className="text-muted-foreground/50">{t.label}:{' '}</span>
                          <AnimatePresence mode="popLayout" initial={false}>
                            <motion.span
                              key={t.value}
                              initial={{ opacity: 0, y: -4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 4 }}
                              transition={{ duration: DURATION_FAST, ease: EASE_EXPO_OUT }}
                              className="text-foreground/70"
                            >
                              {t.value}
                            </motion.span>
                          </AnimatePresence>
                        </motion.span>
                      ))}
                    </AnimatePresence>
                  </span>
                )}
                {/* Chevron (only when enabled) */}
                {section.isChecked && (
                  <ChevronDown className={cn(
                    "w-4 h-4 text-muted-foreground transition-transform duration-200 flex-shrink-0 ml-auto",
                    isExpanded && "rotate-180"
                  )} />
                )}
              </button>

              {/* Right: switch (separate from expand button) */}
              <div className="flex-shrink-0 ml-2">
                <Switch
                  checked={section.isChecked}
                  onCheckedChange={section.onCheckedChange}
                  aria-label={`Enable ${section.title}`}
                />
              </div>
            </div>

            {/* Mobile-only summary tokens (below header bar, visible when collapsed) */}
            {hasSummary && !isExpanded && (
              <div className="sm:hidden px-3 pb-1.5 -mt-1 bg-muted">
                <span className="text-[11px] sm:text-[12px] font-mono text-muted-foreground">
                  {section.summaryTokens!.map((t, i) => (
                    <React.Fragment key={t.label}>
                      {i > 0 && <span className="text-muted-foreground/30 mx-1">·</span>}
                      <span className="text-muted-foreground/50">{t.label}:{' '}</span>
                      <span className="text-foreground/70">{t.value}</span>
                    </React.Fragment>
                  ))}
                </span>
              </div>
            )}

            {/* Collapsible content */}
            <AnimatedCollapse open={isExpanded}>
              <div className="px-3 pt-3 pb-3 min-w-0 overflow-x-hidden">
                <p className="text-[12px] sm:text-[13px] text-muted-foreground mb-3">
                  {section.description}
                </p>
                <Component {...commonProps} noCard hideTitle hideToggle />
              </div>
            </AnimatedCollapse>
          </div>
        );
      })}
    </div>
  );

  if (noCard) {
    return (
      <div className={cn("animate-fade-in w-full text-foreground", className)}>
        {headerBars}
      </div>
    );
  }

  // Card variant (client config)
  const borderColor = isClientVariant
    ? (hasAnyCustomization ? 'border-l-primary' : '')
    : 'border-l-primary';

  const badgeText = isClientVariant
    ? `${customizationCount} Custom`
    : 'Active';

  return (
    <Card className={cn(
      "animate-fade-in rounded w-full equipment-panel",
      borderColor && `border-l-4 ${borderColor}`,
      className
    )}>
      <CardContent className="space-y-6">
        <div className="space-y-2 text-center">
          <div className="flex items-center justify-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded bg-surface-2 border border-border">
              {isClientVariant && hasAnyCustomization ? (
                <AlertTriangle className="w-4 h-4 text-primary" />
              ) : (
                <Settings className="w-4 h-4 text-primary" />
              )}
            </div>
            <h2 className="heading-label text-lg text-foreground">{title}</h2>
            {hasAnyCustomization && (
              <Badge variant="secondary" className="rounded bg-primary/10 text-primary border-primary/20">
                {badgeText}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground px-2 max-w-lg mx-auto leading-relaxed">
            {description}
          </p>
        </div>

        {headerBars}

        {isClientVariant && hasAnyCustomization && onRevertAll && (
          <div className="pt-6 border-t border-border">
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <AlertTriangle className="w-4 h-4" />
                <span>{customizationCount} section{customizationCount !== 1 ? 's' : ''} customized for this client</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onRevertAll}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Revert All to Profile Defaults
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
